import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { combineLatest, map, Observable, Subject, take } from 'rxjs';
import { Router } from '@angular/router';
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { Steps } from '@app/components/stepper/stepper.component';
import { PortalLeadNode, PortalOrderNode } from '@app/services/api/api.types';
import { PortalLeadService } from '@app/services/portal-lead/portal-lead.service';
import { PortalOrderService } from '@app/services/portal-order/portal-order.service';
import { StateService } from '@app/services/state/state.service';
import { AuthService, User } from '@app/services/auth/auth.service';
import { PortalTrackingEvents } from '@app/utils/tracking-events';
import { Tracking } from '@app/services/tracking/tracking.service';
import { PortalRoutes } from '@app/app-routing.module';
import { ApiService } from '@app/services/api/api.service';
import { VerificationModalService } from '@app/services/verification-modal.service';
import { AuthorizedDataService } from '@app/services/authorized-data/authorized-data.service';
import { CommonDataService } from '@app/services/common-data/common-data.service';

@Component({
  selector: 'thank-you-page',
  templateUrl: './thank-you-page.component.html',
  styleUrls: ['./thank-you-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ThankYouPageComponent implements OnInit, OnChanges {
  Steps = Steps;
  CognitoHostedUIIdentityProvider = CognitoHostedUIIdentityProvider;

  portalLead$: Observable<PortalLeadNode | null>;
  user$: Subject<User | null>;

  portalOrder: PortalOrderNode;

  form: FormGroup = new FormGroup({
    email: new FormControl('', { validators: [Validators.required, Validators.email] }),
  });

  loading: boolean;

  activationError: string | null;

  isNewUser: boolean;

  hasSelectedProtection: boolean;

  redirectUrl: string;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private authorizedDataService: AuthorizedDataService,
    private verificationModalService: VerificationModalService,
    private portalLeadService: PortalLeadService,
    private portalOrderService: PortalOrderService,
    private stateService: StateService,
    private authService: AuthService,
    private apiService: ApiService,
    private router: Router,
    protected commonDataService: CommonDataService,
  ) {}

  ngOnInit() {
    this.user$ = this.authService.user$;

    this.initSelectedProtection();

    this.getPortalLead();

    Tracking.track(PortalTrackingEvents.portalCheckoutSuccess);

    this.redirectUrl = `${window.location.origin}/travelers`;
  }

  initSelectedProtection() {
    this.hasSelectedProtection =
      typeof this.stateService.getState().cancelForAnyReason === 'number' ||
      typeof this.stateService.getState().disruptionProtection === 'number' ||
      !!this.stateService.getState().bundle;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (typeof changes['activationError']?.currentValue === 'string') {
      this.loading = false;

      this.form.enable();
      this.form.setErrors({});
    }
  }

  getPortalLead() {
    this.portalLead$ = combineLatest([
      this.portalLeadService.portalLead$,
      this.portalOrderService.portalOrder$,
    ]).pipe(
      map(([portalLead, portalOrder]) => {
        if (portalLead) {
          if (portalLead.isNewUser) {
            this.isNewUser = true;
          }
        }

        if (portalOrder) {
          this.portalOrder = portalOrder;
        }

        return portalLead;
      }),
    );
  }

  focus() {
    Tracking.track(PortalTrackingEvents.portalCheckoutContinueWithEmailInputFocused);
  }

  submit() {
    this.loading = true;

    this.form.disable();

    const value = this.form.get('email')?.value as string;

    this.sendEmail(value?.toLowerCase());

    Tracking.track(PortalTrackingEvents.portalCheckoutContinueWithEmailInputSubmitted);
  }

  sendEmail(email: string): void {
    this.activationError = '';

    const portalLink = this.stateService.getState().portalLink;

    const variables = { portalLink, email };

    this.apiService
      .sendPasswordToEmail(variables)
      .pipe(take(1))
      .subscribe(({ errors }) => {
        if (errors?.length) {
          this.activationError = errors[0].message;
        } else {
          const activation = {
            emailMasked: email,
            emailSentAt: new Date().toUTCString(),
          };

          this.verificationModalService.activation$.next({ email, activation });
        }

        this.changeDetectorRef.detectChanges();
      });
  }

  continueWith(provider: CognitoHostedUIIdentityProvider): void {
    Tracking.track(
      provider === CognitoHostedUIIdentityProvider.Facebook
        ? PortalTrackingEvents.portalCheckoutContinueWithEmailInputSubmitted
        : PortalTrackingEvents.portalCheckoutContinueWithGooglePressed,
    );

    void this.authService.federatedSignIn(provider, this.redirectUrl);
  }

  continue() {
    const id = this.authorizedDataService.getAuthorizedDataValue()?.passengers?.[0]?.id;

    if (id) {
      void this.router.navigate([`/${PortalRoutes.Travelers}`, id]);
    }
  }
}
